import {Psychology as PsychologyIcon, Refresh as RefreshIcon, Send as SendIcon} from "@mui/icons-material";
import {Alert, Box, Button, Container, Divider, Grid, Paper, Typography} from "@mui/material";
import {useEffect, useState} from "react";
import useRefreshToken from "../hooks/useRefreshToken";
import {aiService, utilsService} from "../services/api";
import ComparisonModal from "./ComparisonModal";
import FileUploadCard from "./FileUploadCard";
import ProcessorSelector from "./ProcessorSelector";
import ResultCard from "./ResultCard";
import SchemaManager from "./SchemaManager";
import TextInputField from "./TextInputField";

const AIAnalysisForm = () => {
  const {refreshToken} = useRefreshToken();
  const [selectedFile, setSelectedFile] = useState(null);
  const [textInput, setTextInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [schemaContent, setSchemaContent] = useState(null);
  const [saveMessage, setSaveMessage] = useState("");
  const [processorType, setProcessorType] = useState("ts");
  const [hasBothResults, setHasBothResults] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [retryAttempt, setRetryAttempt] = useState(0);

  // Verificar al cargar si existen ambos resultados
  useEffect(() => {
    checkBothResults();
  }, []);

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setError("");
  };

  const handleTextChange = (event) => {
    setTextInput(event.target.value);
    setError("");
  };

  const handleProcessorChange = (event) => {
    setProcessorType(event.target.value);
    setError("");
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError("Debe proporcionar un archivo para analizar");
      return;
    }

    setIsSubmitting(true);
    setRetryAttempt(0);
    setError("");
    setResult(null);

    try {
      await refreshToken();

      const fileBase64 = await utilsService.fileToBase64(selectedFile);

      const payload = {
        file: fileBase64
      };

      if (schemaContent) {
        payload.schema = schemaContent;
      }

      if (processorType === "gemini" && textInput.trim()) {
        payload.prompt = textInput.trim();
      }

      const MAX_RETRIES = processorType === "gemini" ? 5 : 1;
      let response;
      let lastError;
      const startTime = Date.now();

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        setRetryAttempt(attempt);
        try {
          response = await aiService.analyzeContent(payload, processorType);
          break;
        } catch (err) {
          lastError = err;
          if (attempt < MAX_RETRIES) {
            await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
          }
        }
      }

      if (!response) {
        throw lastError;
      }

      const endTime = Date.now();

      const processingTime = response.result?.processingTime
        ? `${(parseInt(response.result.processingTime) / 1000).toFixed(2)}s`
        : `${((endTime - startTime) / 1000).toFixed(2)}s`;

      setResult({
        analysis: response.message || "Análisis completado exitosamente",
        confidence: response.confidence || 95,
        timestamp: new Date().toISOString(),
        details: {
          fileProcessed: selectedFile ? selectedFile.name : null,
          textLength: textInput.length,
          processingTime: processingTime,
          ...response.details
        },
        rawResponse: response
      });
    } catch (err) {
      console.error("Error al enviar para análisis:", err);
      setError(err.message || "Error al procesar la solicitud. Por favor, inténtelo de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setTextInput("");
    setResult(null);
    setError("");
    setSaveMessage("");
    setProcessorType("ts");
  };

  const handleCopyResult = () => {
    if (!result) return;

    try {
      const resultText = JSON.stringify(result.rawResponse, null, 2);
      navigator.clipboard.writeText(resultText);
      setSaveMessage("📋 Resultado copiado al portapapeles");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (err) {
      setError("❌ Error al copiar al portapapeles: " + err.message);
      console.error(err);
    }
  };

  const handleSaveResult = () => {
    if (!result) return;

    try {
      const aiType = result.rawResponse?.input?.aiType || processorType;
      const storageKey = `invoiceResult_${aiType}`;

      const dataToSave = {
        result: result.rawResponse,
        timestamp: result.timestamp,
        fileName: result.details.fileProcessed,
        aiType: aiType
      };

      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      setSaveMessage(`✅ Resultado guardado en localStorage (IA: ${aiType.toUpperCase()})`);
      setTimeout(() => setSaveMessage(""), 3000);

      console.log("Resultado guardado:", {
        key: storageKey,
        timestamp: dataToSave.timestamp,
        aiType: aiType
      });

      checkBothResults();
    } catch (err) {
      setError("❌ Error al guardar resultado: " + err.message);
      console.error(err);
    }
  };

  const checkBothResults = () => {
    try {
      const tsResult = localStorage.getItem("invoiceResult_ts");
      const geminiResult = localStorage.getItem("invoiceResult_gemini");
      setHasBothResults(!!tsResult && !!geminiResult);
    } catch (err) {
      console.error("Error verificando resultados:", err);
      setHasBothResults(false);
    }
  };

  const handleCompareResults = () => {
    try {
      const tsData = localStorage.getItem("invoiceResult_ts");
      const geminiData = localStorage.getItem("invoiceResult_gemini");

      if (!tsData || !geminiData) {
        setError("⚠️ No se encontraron ambos resultados guardados");
        return;
      }

      const tsParsed = JSON.parse(tsData);
      const geminiParsed = JSON.parse(geminiData);

      const tsDataExtracted = tsParsed.result?.result?.data || {};
      const geminiDataExtracted = geminiParsed.result?.result?.data || {};

      const differences = findDifferences(tsDataExtracted, geminiDataExtracted);

      setComparisonData({
        ts: tsParsed,
        gemini: geminiParsed,
        differences: differences
      });

      setShowComparison(true);
    } catch (err) {
      setError("❌ Error al comparar resultados: " + err.message);
      console.error(err);
    }
  };

  const findDifferences = (obj1, obj2, path = "") => {
    const differences = new Set();

    const compare = (o1, o2, currentPath) => {
      if (typeof o1 !== typeof o2) {
        differences.add(currentPath);
        return;
      }

      if (o1 === null || o2 === null) {
        if (o1 !== o2) differences.add(currentPath);
        return;
      }

      if (typeof o1 !== "object") {
        if (o1 !== o2) differences.add(currentPath);
        return;
      }

      const keys1 = Object.keys(o1 || {});
      const keys2 = Object.keys(o2 || {});
      const allKeys = new Set([...keys1, ...keys2]);

      allKeys.forEach((key) => {
        const newPath = currentPath ? `${currentPath}.${key}` : key;
        if (!(key in o1) || !(key in o2)) {
          differences.add(newPath);
        } else {
          compare(o1[key], o2[key], newPath);
        }
      });
    };

    compare(obj1, obj2, path);
    return differences;
  };

  const handleCloseComparison = () => {
    setShowComparison(false);
  };

  const isFormValid = selectedFile;

  return (
    <Container maxWidth="lg" sx={{py: 4}}>
      <Paper elevation={2} sx={{p: 4}}>
        <Box sx={{mb: 4, textAlign: "center"}}>
          <PsychologyIcon sx={{fontSize: 48, color: "primary.main", mb: 2}} />
          <Typography variant="h4" component="h1" gutterBottom>
            Análisis con IA
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Sube una imagen o PDF y/o proporciona texto para análisis con inteligencia artificial
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} lg={4}>
            <SchemaManager
              isSubmitting={isSubmitting}
              onSchemaContentChange={setSchemaContent}
              onError={setError}
              onSaveMessage={setSaveMessage}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <FileUploadCard onFileSelect={handleFileSelect} />
          </Grid>

          <Grid item xs={12} lg={4}>
            <ProcessorSelector
              processorType={processorType}
              onChange={handleProcessorChange}
              isSubmitting={isSubmitting}
            />
          </Grid>

          {processorType === "gemini" && (
            <Grid item xs={12} lg={6}>
              <TextInputField textInput={textInput} onChange={handleTextChange} isSubmitting={isSubmitting} />
            </Grid>
          )}
        </Grid>

        <Divider sx={{my: 4}} />

        <Box sx={{textAlign: "center", mb: 3}}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            startIcon={isSubmitting ? <RefreshIcon /> : <SendIcon />}
            sx={{
              minWidth: 200,
              mr: 2,
              mb: {xs: 2, sm: 0},
              "& .MuiButton-startIcon": {
                animation: isSubmitting ? "spin 1s linear infinite" : "none"
              },
              "@keyframes spin": {
                from: {transform: "rotate(0deg)"},
                to: {transform: "rotate(360deg)"}
              }
            }}
          >
            {isSubmitting
              ? retryAttempt > 1
                ? `Reintentando... (${retryAttempt}/5)`
                : "Analizando..."
              : "Analizar con IA"}
          </Button>

          <Button
            variant="outlined"
            size="large"
            onClick={handleReset}
            disabled={isSubmitting}
            sx={{minWidth: 120, mr: 2, mb: {xs: 2, sm: 0}}}
          >
            Limpiar
          </Button>

          {hasBothResults && (
            <Button
              variant="contained"
              size="large"
              color="secondary"
              onClick={handleCompareResults}
              disabled={isSubmitting}
              sx={{minWidth: 120, mb: {xs: 2, sm: 0}}}
            >
              Comparar IAs
            </Button>
          )}
        </Box>

        {saveMessage && (
          <Alert severity="success" sx={{my: 3}}>
            {saveMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{mb: 3}}>
            {error}
          </Alert>
        )}

        <ResultCard result={result} processorType={processorType} onCopy={handleCopyResult} onSave={handleSaveResult} />
      </Paper>

      <ComparisonModal open={showComparison} onClose={handleCloseComparison} comparisonData={comparisonData} />
    </Container>
  );
};

export default AIAnalysisForm;
